import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Search, ChevronRight } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';
import Button from './components/Button';

const Orders = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');

    const orders = [
        {
            id: 'NAK-2026-001',
            date: '15 Mar 2026',
            status: 'delivered',
            total: 8999,
            items: [
                { name: 'Chanderi Silk Saree', size: 'M', qty: 1, price: 8999, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' }
            ]
        },
        {
            id: 'NAK-2026-002',
            date: '12 Mar 2026',
            status: 'shipped',
            total: 12998,
            items: [
                { name: 'Banarasi Kurta Set', size: 'L', qty: 2, price: 6499, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' }
            ]
        },
        {
            id: 'NAK-2026-003',
            date: '8 Mar 2026',
            status: 'processing',
            total: 18500,
            items: [
                { name: 'Silk Blend Lehenga', size: 'S', qty: 1, price: 18500, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' }
            ]
        },
    ];

    const statusConfig = {
        processing: { label: 'Processing', icon: Clock, color: 'text-[#2563EB]', bg: 'bg-blue-50' },
        shipped: { label: 'Shipped', icon: Truck, color: 'text-amber-600', bg: 'bg-amber-50' },
        delivered: { label: 'Delivered', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
    };

    const filters = ['all', 'processing', 'shipped', 'delivered'];

    const filteredOrders = orders.filter(order => {
        const matchesFilter = activeFilter === 'all' || order.status === activeFilter;
        const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    return (
        <div className="w-full min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <div className="pt-20 md:pt-24 pb-8 bg-white border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-0.5 bg-[#2563EB]" />
                        <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">Track Orders</p>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">My Orders</h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search & Filters */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="relative w-full sm:w-64">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                        <input
                            type="text"
                            placeholder="Search by order ID"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E2E8F0] rounded-none focus:outline-none focus:border-[#2563EB] bg-white"
                        />
                    </div>
                    <div className="flex gap-2">
                        {filters.map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setActiveFilter(filter)}
                                className={`px-4 py-2 text-xs font-medium tracking-wider uppercase rounded-none border transition-all duration-300 ${
                                    activeFilter === filter
                                        ? 'bg-[#2563EB] border-[#2563EB] text-white'
                                        : 'bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]'
                                }`}
                            >
                                {filter === 'all' ? 'All' : filter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Orders List */}
                {filteredOrders.length === 0 ? (
                    <div className="text-center py-20 bg-white border border-[#E2E8F0] rounded-none">
                        <Package size={48} className="mx-auto mb-4 text-[#E2E8F0]" />
                        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">No orders found</h2>
                        <p className="text-sm text-[#64748B] mb-6 font-light">Start shopping to see your orders here</p>
                        <Button variant="primary" onClick={() => navigate('/template9/shop')}>
                            Browse Collection
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredOrders.map((order) => {
                            const status = statusConfig[order.status];
                            const StatusIcon = status.icon;
                            return (
                                <div key={order.id} className="bg-white border border-[#E2E8F0] rounded-none p-4 sm:p-6 relative">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                                    {/* Header */}
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 pb-4 border-b border-[#E2E8F0]">
                                        <div>
                                            <p className="text-sm font-semibold text-[#0F172A]">{order.id}</p>
                                            <p className="text-xs text-[#64748B] font-light">{order.date}</p>
                                        </div>
                                        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${status.bg} rounded-none`}>
                                            <StatusIcon size={14} className={status.color} />
                                            <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
                                        </div>
                                    </div>

                                    {/* Items */}
                                    {order.items.map((item, idx) => (
                                        <div key={idx} className="flex gap-4 mb-4">
                                            <img src={item.image} alt={item.name} className="w-16 h-20 object-cover rounded-none flex-shrink-0" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-[#0F172A]">{item.name}</p>
                                                <p className="text-xs text-[#64748B] font-light">Size: {item.size} | Qty: {item.qty}</p>
                                                <p className="text-sm font-semibold text-[#2563EB] mt-1">{'\u20B9'}{item.price.toLocaleString('en-IN')}</p>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Footer */}
                                    <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
                                        <p className="text-sm font-semibold text-[#0F172A]">
                                            Total: <span className="text-[#2563EB]">{'\u20B9'}{order.total.toLocaleString('en-IN')}</span>
                                        </p>
                                        <button className="inline-flex items-center gap-1 text-xs font-medium text-[#2563EB] hover:text-[#1D4ED8] bg-transparent border-none cursor-pointer uppercase tracking-wider">
                                            View Details <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Orders;
